import { useEffect, useState } from 'react';
import { Globe, Save } from 'lucide-react';
import { settingsAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { COMMON_TIMEZONES, browserTimeZone, isValidTimeZone } from '../lib/tz.js';
import { formatDateTimeWithZone } from '../lib/format.js';

// The tenant's operating TIMEZONE — the single clock this account runs on.
//
// Timestamps are stored in UTC and always will be. This decides two things:
//   - what every time on the dashboard is displayed in, and
//   - what instant a wall-clock schedule ("call at 9:10am") actually means.
// Saving updates the AuthContext tenant, which re-primes the display zone, so the
// whole app re-renders in the new zone without a reload.

export default function TimezoneCard() {
  const { tenant, selectedTenant, isOwner, updateTenant, user } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  const isAdmin = user?.role === 'admin';

  const [tz, setTz] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedOk, setSavedOk] = useState(false);
  const [notReady, setNotReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    settingsAPI
      .getTimezone()
      .then((r) => {
        if (cancelled) return;
        // null = never chosen. Keep the select EMPTY rather than pre-filling a
        // plausible zone — a pre-filled value reads as "already handled" and the
        // whole point is that they make the choice deliberately.
        const v = r?.data?.timezone ?? null;
        setTz(v || '');
        setSaved(v);
      })
      .catch((err) => {
        if (cancelled) return;
        // Route not deployed yet — degrade to a quiet note rather than an error.
        if (err.status === 404) setNotReady(true);
        else setError(err.message || 'Failed to load the timezone.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // Refetch when an owner switches tenants — the zone belongs to the tenant.
  }, [shownTenant?.id]);

  const save = async () => {
    setError('');
    setSavedOk(false);
    if (!isValidTimeZone(tz)) {
      setError('Pick a timezone.');
      return;
    }
    setSaving(true);
    try {
      const r = await settingsAPI.updateTimezone(tz);
      const next = r?.data?.timezone || tz;
      setSaved(next);
      setTz(next);
      // Re-primes the display zone + re-renders every timestamp in the app.
      updateTenant({ timezone: next });
      setSavedOk(true);
    } catch (err) {
      setError(err.body?.message || err.message || 'Failed to save the timezone.');
    } finally {
      setSaving(false);
    }
  };

  if (notReady) return null;

  const dirty = tz !== (saved || '');
  const browser = browserTimeZone();
  // Never chosen. NOT the same as an explicit UTC, which is a real answer.
  const unset = saved == null;
  const options =
    !tz || COMMON_TIMEZONES.some((o) => o.value === tz)
      ? COMMON_TIMEZONES
      : // A zone set outside this list (any IANA name is valid — the in-call AI or
        // a script may set one) must still show as the current selection rather
        // than silently reading as something else.
        [...COMMON_TIMEZONES, { value: tz, label: tz }];

  return (
    <section className="card mb-8">
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-4 h-4 text-stone-400" />
        <h2 className="text-lg font-semibold">Timezone</h2>
      </div>
      <p className="text-sm text-stone-400 mb-4">
        The clock this account runs on. Every time on the dashboard is shown in it, and a
        scheduled call time means this zone. Changing it never moves an existing call — it
        only changes how times are read and written.
      </p>

      {!loading && unset && (
        <div className="mb-4 rounded-lg border border-amber-700/60 bg-amber-950/30 p-3">
          <p className="text-sm text-amber-200">
            <span className="font-semibold">Pick a timezone to start scheduling calls.</span> Until
            then, scheduling is turned off — a call time has to be 9:10am <em>somewhere</em>, and
            we'd rather ask than guess and dial at 3am. Times below are shown in your computer's
            zone ({browser}) in the meantime.
          </p>
        </div>
      )}

      {error && <p className="text-sm text-rose-400 mb-3">{error}</p>}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="block text-xs text-stone-400 mb-1">Timezone</span>
              <select
                className="field-input"
                value={tz}
                disabled={!isAdmin || saving}
                onChange={(e) => {
                  setTz(e.target.value);
                  setSavedOk(false);
                }}
              >
                {/* Placeholder only while unset — no pre-selected zone to accept by
                    accident. It disappears once a real choice exists. */}
                {!tz && <option value="">Select a timezone…</option>}
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            {isAdmin && (
              <button className="btn-primary" onClick={save} disabled={!dirty || saving}>
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>

          {tz && (
            <p className="text-sm text-stone-500 mt-3">
              Right now that reads{' '}
              <span className="text-stone-300">
                {formatDateTimeWithZone(new Date().toISOString(), tz)}
              </span>
              {tz !== browser && isValidTimeZone(browser) && (
                <>
                  {' '}· your computer says{' '}
                  <span className="text-stone-300">
                    {formatDateTimeWithZone(new Date().toISOString(), browser)}
                  </span>
                </>
              )}
              .
            </p>
          )}

          {!isAdmin && (
            <p className="text-xs text-stone-500 mt-2">Only an admin can change the timezone.</p>
          )}
          {savedOk && !dirty && <p className="text-xs text-emerald-400 mt-2">Saved.</p>}
        </>
      )}
    </section>
  );
}
